import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import { DetectMethodChanges } from '../../utils';
import { parseOptionTicker } from '../../options-common/options.model';
import { CollarsQgCursorService } from '../collars-qg-cursor.service';
import { CollarsAfterStateRow, comboKeyOf } from './collars-after-state-rows';
import {MessageBusService} from '../../message-bus.service';
import {LastQuoteCacheService} from '../../last-quote-cache.service';
import {TradingInstrumentDisplayNameService} from '../../trading-instruments/trading-instrument-display-name.service';
import {OrderPreviewTables} from '../../order-preview/order-preview-tables.component';
import {OrderPreviewMarket} from '../../order-preview/order-preview-market';
import {manualPreviewTables, manualPricingHint} from './collars-preview-tables';

/** What the operator chose in the review pop-up. 'acquire' carries the per-transaction-leg FILL prices
 *  entered in the pop-up's acquisition mode; the Make-Transaction dialog books them (one
 *  RecordCollarsTransaction per fill, in the supplied order — close-before-open per instrument holds
 *  because fills follow the diff's per-group emission order). */
export type CollarsPreviewAction = 'copy-tos' | 'acquire' | 'close';

/** One priced booking leg the acquisition mode hands back (display row -> bookable fill). */
export interface CollarsAcquireFill {
   groupBucketId: string;
   tradingInstrumentCode: string;
   kind: number;
   side: number;
   qty: number;
   fillPx: number;
}

export interface CollarsPreviewResult {
   action: CollarsPreviewAction;
   fills?: CollarsAcquireFill[];   // present iff action === 'acquire'
}

/** Manual review/acquisition wrapper. The PH/A and manual dialogs share the tables and
 * compact presentation. This wrapper preserves whole-transaction pricing/booking, copy/save,
 * diff emission order and QG expiration dates. Market guidance never changes actual fills. */
@Component({
   selector: 'ets-collars-after-state-preview',
   templateUrl: './collars-after-state-preview.component.html',
   styleUrls: ['../../portfolio-hedging-analysis/pha-export-dialog.component.scss', './collars-after-state-preview.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollarsAfterStatePreviewComponent implements OnDestroy {

   constructor(
      private readonly _changeDetector: ChangeDetectorRef,
      private readonly _qgCursor: CollarsQgCursorService,
      private readonly _lastQuoteCache: LastQuoteCacheService,
      private readonly _messageBus: MessageBusService,
      private readonly _displayNames: TradingInstrumentDisplayNameService
   ) {
      this._market = new OrderPreviewMarket(_lastQuoteCache, _messageBus, () => this.repaintTables());
      // QG history mode: the Expiration cells' "(Nd)" DTE counts from the cursor day (pipe fromDate
      // argument). Repaint when that day changes while the pop-up is open — this OnPush view has no
      // other tick to pick it up.
      this._qgCursor.dayChanged$
         .pipe(takeUntil(this._unsub))
         .subscribe(() => {
            if (this.popup && this.popup.visible) {
               this.refreshCursorDays();
               this.repaintTables();
            }
         });
   }

   private readonly _unsub = new Subject<void>();

   private _resolver: (value: CollarsPreviewResult) => void;

   @ViewChild(DxPopupComponent) popup: DxPopupComponent;

   private readonly _market: OrderPreviewMarket;
   tables: OrderPreviewTables[] = [];
   underlyingTitles: string[] = [];
   readonly popupWrapperAttr = {class: 'pha-export-popup'};

   title = 'After State Preview';

   // Wide enough that the three framed Before/Transaction/After panels read as separate blocks
   // (owner allowed extending the dialog width — vrequest 2026-06-10; +80px for the Transaction
   // Type column 2026-06-11).
   width = 1320;

   // Centered number-box text (stable object — an inline template literal would re-create it every CD pass).
   readonly dxCenterTextStyleObj = { style: 'text-align: center' };

   // Footer order per owner (2026-06-10): Copy to TOS, Acquire Immediately, Cancel.
   btnCopyTosText = 'Copy to TOS';
   btnAcquireText = 'Acquire Immediately';
   btnCompleteText = 'Complete Acquisition';
   btnCloseText = 'Cancel';

   previewRows: CollarsAfterStateRow[] = [];

   // TWO-STATE: 'preview' (read-only) or 'acquire' (Fill Px column shown, fills collected in-place).
   mode: 'preview' | 'acquire' = 'preview';

   // Fill prices, index-aligned to previewRows[rowIx].transaction[txnIx] (same display order the fills
   // are handed back in — preserves the diff's close-before-open emission order per group).
   fillPx: number[][] = [];

   get isAcquireMode(): boolean {
      return this.mode === 'acquire';
   }

   // Per-keystroke fill commit (the box sets valueChangeEvent="input"): the canComplete gate reads
   // fillPx, and the DX default 'change' commit left it stale (Complete Acquisition disabled) until
   // the operator blurred the box — this pop-up has no quote tick to hide that in any mode.
   @DetectMethodChanges()
   onFillPxChange(rowIx: number, txnIx: number, value: number): void {
      this.fillPx[rowIx][txnIx] = value;
      this.tables[rowIx].transaction[txnIx].fillPx = value;
   }

   // Complete Acquisition enables only when EVERY transaction leg carries a positive fill.
   get canComplete(): boolean {
      let any = false;
      for (let r = 0; r < this.previewRows.length; r++) {
         const txns = this.previewRows[r].transaction || [];
         for (let t = 0; t < txns.length; t++) {
            any = true;
            const px = (this.fillPx[r] || [])[t];
            if (typeof px !== 'number' || !isFinite(px) || px <= 0) { return false; }
         }
      }
      return any;
   }

   // Render combo separators only when the rows span MORE THAN ONE REAL combo (portfolio scope). Rows
   // without combo context (an unrouted leg's 'Unassigned' section) do NOT count — they must not switch
   // separators on at single-combo scope. Rows arrive already ordered by combo (orderByCombo in the
   // mapper, same comboKeyOf bucketing), so a separator sits wherever consecutive rows change combo.
   get multiCombo(): boolean {
      const seen: { [comboBucketId: string]: true } = {};
      let count = 0;
      for (let i = 0; i < this.previewRows.length; i++) {
         const comboBucketId = this.previewRows[i].comboBucketId;
         if (!comboBucketId || seen[comboBucketId]) { continue; }
         seen[comboBucketId] = true;
         count++;
         if (count > 1) { return true; }
      }
      return false;
   }

   isComboStart(index: number): boolean {
      if (index === 0) { return true; }
      return comboKeyOf(this.previewRows[index - 1]) !== comboKeyOf(this.previewRows[index]);
   }

   // The QG cursor day the leg's Expiration "(Nd)" counts from — keyed by the leg's UNDERLYING (the
   // cursor is per-stream). Undefined outside QG history (the pipe then falls back to the wall clock).
   // parseOptionTicker caches parses, so the per-CD template call stays cheap; asset legs parse to
   // undefined and never reach the lookup.
   qgCursorDayOf(leg: { ticker?: string }): string {
      const t = parseOptionTicker(leg ? leg.ticker : null);
      return t ? this._qgCursor.cursorDayFor(t.underlying) : undefined;
   }

   pricingHint(rowIx: number): string {
      return this.isAcquireMode ? manualPricingHint(this.tables[rowIx]) : '';
   }

   private refreshCursorDays(): void {
      this.tables.forEach(t => [...t.before, ...t.transaction, ...t.after].forEach(l => {
         l.cursorDay = this.qgCursorDayOf(l);
      }));
   }

   private repaintTables(): void {
      // Keep tracked leg objects, but replace each input view so the OnPush shared child repaints.
      this.tables = this.tables.map(t => ({...t}));
      this._changeDetector.detectChanges();
   }

   ngOnDestroy(): void {
      this._market.stop();
      this._unsub.next();
      this._unsub.complete();
   }

   /** Open the review for the supplied rows (always starts in the PREVIEW state). */
   @DetectMethodChanges()
   show(rows: CollarsAfterStateRow[]): Promise<CollarsPreviewResult> {

      this._market.stop();
      this.previewRows = rows || [];
      this.tables = this.previewRows.map(manualPreviewTables);
      this.refreshCursorDays();
      this.underlyingTitles = this.previewRows.map(row => {
         const tickers: string[] = [];
         [...row.before, ...row.transaction, ...row.after].forEach(leg => {
            const option = leg.kind === 1 ? parseOptionTicker(leg.ticker) : null;
            const ticker = option ? option.underlying : leg.ticker;
            if (tickers.indexOf(ticker) < 0) { tickers.push(ticker); }
         });
         return tickers.map(t => {
            const name = this._displayNames.getDisplayNameForTicker(t);
            return name || t;
         }).join(' / ');
      });
      this.mode = 'preview';
      this.fillPx = [];

      this.popup.visible = true;

      setTimeout(() => this._changeDetector.detectChanges());

      return new Promise<CollarsPreviewResult>((res) => {
         this._resolver = res;
      });
   }

   // Cancel: in ACQUISITION mode it returns to the preview state (fills discarded); in preview it closes.
   @DetectMethodChanges()
   onCloseClicked(): void {
      if (this.isAcquireMode) {
         this._market.stop();
         this.mode = 'preview';
         this.tables.forEach(t => t.transaction.forEach(l => l.fillPx = null));
         this.fillPx = [];
         return;
      }
      this.closePreview({ action: 'close' });
   }

   onCopyToTosClicked(): void {
      this.closePreview({ action: 'copy-tos' });
   }

   // Preview state: switch into ACQUISITION mode (the Fill Px column appears in place — no interface
   // switch, same look the Apply-After-State dialog uses).
   @DetectMethodChanges()
   onAcquireClicked(): void {
      this.fillPx = this.previewRows.map(r => (r.transaction || []).map(() => null as number));
      this.mode = 'acquire';
      const legs = [];
      this.tables.forEach(t => t.transaction.forEach(l => {
         l.fillPx = null;
         legs.push(Object.assign(l, {code: l.ticker}));
      }));
      this._market.start(legs);
   }

   // Acquisition state: hand the priced legs back in display order (per-group close-before-open holds).
   onCompleteClicked(): void {
      if (!this.canComplete) { return; }
      const fills: CollarsAcquireFill[] = [];
      for (let r = 0; r < this.previewRows.length; r++) {
         const txns = this.previewRows[r].transaction || [];
         for (let t = 0; t < txns.length; t++) {
            const leg = txns[t];
            fills.push({
               groupBucketId: leg.groupBucketId,
               tradingInstrumentCode: leg.ticker,
               kind: leg.kind,
               side: leg.side,
               qty: leg.qty,
               fillPx: this.fillPx[r][t]
            });
         }
      }
      this.closePreview({ action: 'acquire', fills });
   }

   // The popup can hide WITHOUT a button click (DevExtreme closes on Esc on desktop). If the resolver is
   // still pending, settle it as a plain close so the awaiting Make-Transaction dialog never dangles.
   // Button-driven closes already resolved (closePreview nulls the resolver first), so this is a no-op then.
   @DetectMethodChanges()
   onPopupHidden(): void {
      this._market.stop();
      const resolve = this._resolver;
      this._resolver = null;
      this.mode = 'preview';
      this.fillPx = [];
      if (resolve) {
         resolve({ action: 'close' });
      }
   }

   @DetectMethodChanges()
   private closePreview(result: CollarsPreviewResult): void {
      this._market.stop();
      this.popup.visible = false;
      const resolve = this._resolver;
      this._resolver = null;
      if (resolve) {
         resolve(result);
      }
   }
}
